import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute } from "@angular/router";
import { DatesService } from "@cotecna/dates";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  DialogButton,
  LoadingDialogComponent,
} from "@cotecna/dialogs";
import {
  DeletedSectionResult,
  DuplicatedSectionResult,
  Field,
  FieldDefinitionChangeResult,
  FormResult,
  RenamedSectionResult,
  Section,
} from "@cotecna/dynamic-view-engine";
import { ActionElement } from "@cotecna/material";
import {
  TableAction,
  TableActionResult,
  TableColumn,
  ToggleEvent,
} from "@cotecna/tables";
import moment from "moment";
import { BehaviorSubject } from "rxjs";
import { NaturalFormDialogBase } from "src/app/form-dialog/form-dialog.base";
import {
  COTECNA_USER_KEY,
  FAB_ACTION_ADD_SINGLE_EVENT,
  FAB_ACTION_IMPORT_ITEM_EVENTS_FROM_INSPECTION,
  INSPECTOR_ROLE,
  SHARE_ALL_TIMELOG_MESSAGE,
  SHARE_SINGLE_TIMELOG_MESSAGE,
  UNSHARE_SINGLE_TIMELOG_MESSAGE,
} from "src/app/inspection/constants/constants";
import { ImportEventDialogComponent } from "src/app/inspection/dialogs/import-event-dialog/import-event-dialog.component";
import {
  EVENT_CONTROLS,
  EVENT_SECTIONS,
} from "src/app/inspection/dialogs/timelog-dialog/event-form";
import { InspectionEvent } from "src/app/inspection/model/inspection-event";
import { ItemLight } from "src/app/inspection/model/itemLight";
import { OptionResponse } from "src/app/inspection/model/option-response";
import { TimelogConfig } from "src/app/inspection/permissions-setter/permissions-interface";
import { EventsService } from "src/app/inspection/services/inspection-events-service/events.service";
import { PermissionsService } from "src/app/inspection/services/permissions.service";
import { ItemsService } from "src/app/items/services/items.service";
import { LoginSource, User } from "src/app/session/models/user";
import { StorageService } from "src/app/session/services/storage.service";
import { UserService } from "src/app/session/services/user.service";
import { Option } from "src/app/settings/models/option";

import { EVENTS_TABLE_COLUMNS } from "./events-table-columns";

const editAction = {
  icon: "create",
  name: "edit",
  tooltip: "Edit",
} as TableAction;
const removeAction = {
  icon: "delete_outline",
  name: "remove",
  tooltip: "Remove",
} as TableAction;

@Component({
  selector: "im-inspection-details-timelog",
  templateUrl: "./inspection-details-timelog.component.html",
  styleUrls: ["./inspection-details-timelog.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InspectionDetailsTimelogComponent
  extends NaturalFormDialogBase
  implements OnInit
{
  public speedDialActions: ActionElement[] = [
    {
      icon: "add",
      name: FAB_ACTION_ADD_SINGLE_EVENT,
      text: FAB_ACTION_ADD_SINGLE_EVENT,
      tooltip: FAB_ACTION_ADD_SINGLE_EVENT,
    },
    {
      icon: "cloud_upload",
      name: FAB_ACTION_IMPORT_ITEM_EVENTS_FROM_INSPECTION,
      text: FAB_ACTION_IMPORT_ITEM_EVENTS_FROM_INSPECTION,
      tooltip: FAB_ACTION_IMPORT_ITEM_EVENTS_FROM_INSPECTION,
    },
  ];
  private inspectionId: string = "";
  private inspectionEventOptions: Option[] = [];
  private inspectionEventItems: ItemLight[] = [];
  private readonly NL_PREFIX_ACCOUNT_NAME: string = "NL -";
  public shareTimelogEventStatus = false;
  public isEventExist = false;

  public formSections: Section[] = [];
  public formControls: any[] = [];

  public isLoading: boolean = true;
  public isNLAccount = true;
  public shareAllowed = false;
  public events = new BehaviorSubject<InspectionEvent[]>([]);
  public columns: TableColumn[] = EVENTS_TABLE_COLUMNS;
  public actions: TableAction[] = [];
  public config: TimelogConfig = {
    allowCreate: true,
    allowEdit: true,
    allowRemove: true,
  };

  public constructor(
    private activatedRoute: ActivatedRoute,
    private eventService: EventsService,
    private itemService: ItemsService,
    private datesService: DatesService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private permissionsService: PermissionsService,
    private storageService: StorageService,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super(dialog);
  }

  public async ngOnInit() {
    try {
      this.getInspectionIdFromUrl();
      await this.managePermissions();
      await this.getInspectionEventsOptions();
      await this.getInspectionEventsItems();
      await this.getInspectionEvents();

      const user: User = await this.storageService.get(COTECNA_USER_KEY);
      if (!user?.accountName?.startsWith(this.NL_PREFIX_ACCOUNT_NAME)) {
        this.columns = this.columns.filter((c) => c.dataProperty != "shared");
        this.isNLAccount = false;
      }
      this.shareAllowed = this.userService.shareTimelogAllowed(user);
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  public onFabSpeedDialSelectedAction(actionElement: ActionElement): void {
    const actionName = actionElement.name;
    switch (actionName) {
      case FAB_ACTION_ADD_SINGLE_EVENT:
        this.openAddEventDialog();
        break;
      case FAB_ACTION_IMPORT_ITEM_EVENTS_FROM_INSPECTION:
        this.openImportEventDialog();
        break;
    }
  }

  private async openAddEventDialog() {
    const eventFormInitialized = await this.initEventForm();
    const dialogResult = await this.openFormDialog(
      "event_note",
      "Add event",
      eventFormInitialized.sections,
      eventFormInitialized.controls,
    );

    if (dialogResult?.data) {
      const formData = dialogResult.data;
      const inspectionEvent: InspectionEvent =
        this.mapToInspectionEvent(formData);
      if (formData.name)
        inspectionEvent.optionId = await this.handleEventOption(formData.name);
      await this.eventService.createEvent(inspectionEvent);
      await this.getInspectionEvents();
    }
  }

  private async openImportEventDialog() {
    const dialogRef = this.dialog.open(ImportEventDialogComponent, {
      data: this.inspectionId,
      autoFocus: false,
      disableClose: true,
    });
    const dialogResult = await dialogRef.afterClosed().toPromise();
    if (dialogResult) {
      this.snackBar.open("Event Log has been imported succesfully", "CLOSE", {
        duration: 3000,
        panelClass: ["success-snackbar"],
      });
      await this.getInspectionEvents();
    }
  }

  public async processAction(actionResult: TableActionResult) {
    const event = actionResult.data as InspectionEvent;
    switch (actionResult.action) {
      case editAction:
        await this.editEvent(event);
        break;
      case removeAction:
        this.removeEvent(event);
        break;
    }
  }

  public allowCreate(): boolean {
    return this.config.allowCreate;
  }

  public onFormSubmitted(result: FormResult) {
    throw new Error("Method not implemented.");
  }
  public onFieldChange(field: Field) {
    throw new Error("Method not implemented.");
  }
  public onRenameSection(result: RenamedSectionResult) {
    throw new Error("Method not implemented.");
  }
  public onDuplicateSection(result: DuplicatedSectionResult) {
    throw new Error("Method not implemented.");
  }
  public onDeleteSection(result: DeletedSectionResult) {
    throw new Error("Method not implemented.");
  }
  public onRenameField(result: FieldDefinitionChangeResult) {
    throw new Error("Method not implemented.");
  }
  public onDuplicateField(result: FieldDefinitionChangeResult) {
    throw new Error("Method not implemented.");
  }
  public onDeleteField(result: FieldDefinitionChangeResult) {
    throw new Error("Method not implemented.");
  }
  public onPhotoTaken(result: any) {
    throw new Error("Method not implemented.");
  }
  public onFullPictureRequested(result: string) {
    throw new Error("Method not implemented.");
  }

  public async onToggleChanged(toggleEvent: ToggleEvent): Promise<void> {
    if (toggleEvent.event.checked) {
      this.shareTimelog(toggleEvent);
    } else {
      this.unshareTimelog(toggleEvent);
    }
  }

  private async shareTimelog(toggleEvent: ToggleEvent): Promise<void> {
    const confirmShareDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Share Timelog Event on External Sources",
        message: `Confirm to share the timelog: "${toggleEvent.row.name}" on external sources with client`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmShareDialog
      .afterClosed()
      .toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        SHARE_SINGLE_TIMELOG_MESSAGE,
      );
      try {
        await this.eventService.shareTimelog(
          toggleEvent.row.eventId,
          this.inspectionId,
        );
      } finally {
        loadingDialogRef.close();
      }
    } else {
      toggleEvent.event.source.checked = false;
    }
  }

  private async unshareTimelog(toggleEvent: ToggleEvent) {
    const confirmNoShareDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "warn",
        },
        title: "Stop sharing Timelog Event on external sources",
        message: `Confirm to stop sharing timelog event: "${toggleEvent.row.name}" on external sources with client`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmNoShareDialog
      .afterClosed()
      .toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        UNSHARE_SINGLE_TIMELOG_MESSAGE,
      );
      try {
        await this.eventService.unshareTimelog(
          toggleEvent.row.eventId,
          this.inspectionId,
        );
      } finally {
        loadingDialogRef.close();
      }
    } else {
      toggleEvent.event.source.checked = true;
    }
  }

  public async onTimeLogsChanged(
    toggleEvent: MatSlideToggleChange,
  ): Promise<void> {
    if (toggleEvent.source.checked) {
      this.shareAllTimelogs(toggleEvent);
    } else {
      this.unshareAllTimelogs(toggleEvent);
    }
  }

  private async shareAllTimelogs(
    toggleEvent: MatSlideToggleChange,
  ): Promise<void> {
    const confirmShareDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Share all Timelog Events on external sources",
        message: `All Timelog Events on this inspection are going to be shared with client at Customer Portal.\n\nAll new Timelog events to be added will be also shared by default.\n\nDo you want to confirm?`,
      },
      autoFocus: false,
    });
    const confirmDialogResult = await confirmShareDialog
      .afterClosed()
      .toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        SHARE_ALL_TIMELOG_MESSAGE,
      );
      try {
        await this.eventService.shareAllTimelog(this.inspectionId);
        this.shareTimelogEventStatus = true;
        this.getInspectionEvents();
      } finally {
        loadingDialogRef.close();
        this.snackBar.open(
          "All timelog events has been shared with external source",
          "CLOSE",
          { duration: 3000, panelClass: ["success-snackbar"] },
        );
      }
    } else {
      toggleEvent.source.checked = false;
    }
  }

  private async unshareAllTimelogs(toggleEvent: MatSlideToggleChange) {
    const confirmNoShareDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "warn",
        },
        title: "Stop sharing all Timelog Events on external sources",
        message: `Stop sharing all timelog events on this inspection with client at Customer Portal.\n\nDo you want to confirm?`,
      },
      autoFocus: false,
    });
    const confirmDialogResult = await confirmNoShareDialog
      .afterClosed()
      .toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        SHARE_ALL_TIMELOG_MESSAGE,
      );
      try {
        await this.eventService.unshareAllTimelog(this.inspectionId);
        this.shareTimelogEventStatus = false;
        this.getInspectionEvents();
      } finally {
        loadingDialogRef.close();
        this.snackBar.open(
          "All timelog events has been stop sharing with external source",
          "CLOSE",
          { duration: 3000, panelClass: ["success-snackbar"] },
        );
      }
    } else {
      toggleEvent.source.checked = true;
    }
  }

  private getInspectionIdFromUrl(): void {
    this.activatedRoute.parent?.params.subscribe((params) => {
      this.inspectionId = params["id"];
    });
  }

  private async managePermissions(): Promise<void> {
    const permissions = await this.permissionsService.getPermissions();
    this.config = permissions.setTimelogConfig();
    const user: User = await this.storageService.get(COTECNA_USER_KEY);

    if (
      user.loginSource == LoginSource.external &&
      user.roles &&
      user.roles.includes(INSPECTOR_ROLE)
    ) {
      this.config.allowCreate = true;
      this.config.allowEdit = true;
      if (user?.accountName?.startsWith(this.NL_PREFIX_ACCOUNT_NAME)) {
        this.config.allowRemove = true;
      }
    } else {
      if (this.config?.isReadOnly) {
        this.columns = this.columns.filter((c) => c.dataProperty != "shared");
      }
    }

    if (this.config?.allowEdit) {
      this.actions.push(editAction);
    }

    if (this.config?.allowRemove) {
      this.actions.push(removeAction);
    }
  }

  private async initEventForm(
    inspectionEvent?: InspectionEvent,
  ): Promise<{ sections: Section[]; controls: any }> {
    this.formSections = JSON.parse(JSON.stringify(EVENT_SECTIONS));
    this.formControls = JSON.parse(JSON.stringify(EVENT_CONTROLS));

    const eventOptions = this.inspectionEventOptions.map((option) => {
      return { id: option.id, name: option.appEventName };
    });
    const optionControl = this.formControls.find(
      (control) => control.id === "option",
    );
    optionControl.options = eventOptions;

    const eventItems = this.inspectionEventItems.map((item) => {
      return { id: item.id, name: item.name };
    });
    const itemControl = this.formControls.find((item) => item.id === "item");
    itemControl.options = eventItems;

    if (!this.inspectionEventItems?.length) {
      const itemField = this.formSections[0].fields.find(
        (field) => field.id === "item",
      );
      if (itemField) itemField.visible = false;
    }

    if (inspectionEvent?.startDate) {
      const startDateField = this.formSections[0].fields.find(
        (field) => field.id === "startDate",
      );
      startDateField.value = inspectionEvent.startDate.toString();
      startDateField.valid = true;
    }
    if (inspectionEvent?.endDate) {
      const endDateField = this.formSections[0].fields.find(
        (field) => field.id === "endDate",
      );
      endDateField.value = inspectionEvent.endDate.toString();
      endDateField.valid = true;

      if (inspectionEvent?.startDate == inspectionEvent?.endDate) {
        const sameStartAndEndDateField = this.formSections[0].fields.find(
          (field) => field.id === "sameStartAndEndDate",
        );
        sameStartAndEndDateField.value = true;
        endDateField.readOnly = true;
      }
    }
    if (inspectionEvent?.optionId) {
      const optionField = this.formSections[0].fields.find(
        (field) => field.id === "option",
      );
      optionField.value = inspectionEvent.optionId;
      optionField.readableValue =
        optionControl.options.find((option) => option.id === optionField.value)
          ?.name ?? null;
      optionField.valid = true;
    }
    if (inspectionEvent?.itemId) {
      const itemField = this.formSections[0].fields.find(
        (field) => field.id === "item",
      );
      itemField.value = inspectionEvent.itemId;
      itemField.readableValue =
        itemControl.options.find((item) => item.id === itemField.value)?.name ??
        null;
      itemField.valid = true;
    }
    if (inspectionEvent?.name) {
      const nameField = this.formSections[0].fields.find(
        (field) => field.id === "name",
      );
      nameField.value = inspectionEvent?.name;
      nameField.valid = true;
    }
    if (inspectionEvent?.remarks) {
      const remarksField = this.formSections[0].fields.find(
        (field) => field.id === "remarks",
      );
      remarksField.value = inspectionEvent?.remarks;
      remarksField.valid = true;
    }
    const printinreport = this.formSections[0].fields.find(
      (field) => field.id === "printInReport",
    );
    printinreport.value = inspectionEvent?.printInReport;
    printinreport.valid = true;

    return { sections: this.formSections, controls: this.formControls };
  }

  private async handleEventOption(optionName: string): Promise<string> {
    const existingEventOption = this.findEventOptionByName(optionName);
    if (existingEventOption) {
      return existingEventOption.id;
    } else {
      const result: OptionResponse = await this.eventService.addOption(
        this.inspectionId,
        optionName,
      );
      const newOption = {
        id: result.optionId,
        appEventName: optionName,
        reportEventName: "",
      };
      this.inspectionEventOptions.push(newOption);
      return result.optionId;
    }
  }

  private async getInspectionEvents(): Promise<void> {
    const events = await this.eventService.getEventList(this.inspectionId);
    events.forEach((event) => this.mapEventData(event));
    events.sort(this.sortAlgorithm);
    this.events.next(events);
    if (events.length > 0) {
      this.isEventExist = true;
    } else {
      this.isEventExist = false;
    }
    try {
      this.shareTimelogEventStatus =
        await this.eventService.getShareTimelogEventStatus(this.inspectionId);
    } catch {
      this.shareTimelogEventStatus = false;
    }
  }

  private mapEventData(event: InspectionEvent): void {
    if (event?.optionId) {
      event.name = this.inspectionEventOptions?.find(
        (option) => option.id === event.optionId,
      )?.appEventName;
    }
    if (event?.startDate) {
      event.startDateToShow =
        new Date(event.startDate).toLocaleDateString() +
        " " +
        moment(event.startDate).format("HH:mm");
    }
    if (event?.endDate) {
      event.endDateToShow =
        new Date(event.endDate).toLocaleDateString() +
        " " +
        moment(event.endDate).format("HH:mm");
    }
    if (event?.itemId) {
      event.itemName = this.inspectionEventItems?.find(
        (item) => item.id === event.itemId,
      )?.name;
    }
    if (event.printInReport == undefined) {
      event.printReport = "NA";
    } else {
      event.printReport = event.printInReport == true ? "Yes" : "No";
    }
  }

  private sortAlgorithm(a: InspectionEvent, b: InspectionEvent) {
    if (a.startDate > b.startDate) return -1;
    else if (a.startDate < b.startDate) return 1;
    else {
      if (a.endDate > b.endDate || !b.endDate) return -1;
      else if (a.endDate < b.endDate || !a.endDate) return 1;
      else {
        if (a.name > b.name) return -1;
        else if (a.name < b.name) return 1;
        else return 0;
      }
    }
  }

  private async removeEvent(event: InspectionEvent): Promise<void> {
    const dialogResult = await this.dialog
      .open(ConfirmDialogComponent, {
        autoFocus: false,
        disableClose: true,
        data: <ConfirmDialogData>{
          cancelButton: <DialogButton>{
            text: "Cancel",
          },
          icon: "warning",
          okButton: <DialogButton>{
            text: "Remove",
            color: "warn",
          },
          title: `Remove Event: ${event.name}`,
          message: "Do you want to remove the selected event?",
        },
      })
      .afterClosed()
      .toPromise();

    if (dialogResult) {
      await this.eventService.deleteEvent(event.eventId, this.inspectionId);
      await this.getInspectionEvents();
    }
  }

  private async editEvent(event: InspectionEvent): Promise<void> {
    const formInitialized = await this.initEventForm(event);
    const dialogResult = await this.openFormDialog(
      "event_note",
      "Edit event",
      formInitialized.sections,
      formInitialized.controls,
    );

    if (dialogResult?.data) {
      const formData = dialogResult.data;
      const inspectionEvent = this.mapToInspectionEvent(formData);
      inspectionEvent.eventId = event.eventId;
      inspectionEvent.shared = event.shared;
      if (formData.name)
        inspectionEvent.optionId = await this.handleEventOption(formData.name);
      await this.eventService.editEvent(inspectionEvent);
      this.getInspectionEvents();
    }
  }

  private async getInspectionEventsOptions(): Promise<Option[]> {
    if (!this.inspectionEventOptions?.length) {
      this.inspectionEventOptions = await this.eventService.getEventsOptions(
        this.inspectionId,
      );
    }
    return this.inspectionEventOptions;
  }

  private async getInspectionEventsItems(): Promise<void> {
    this.inspectionEventItems = await this.itemService.getInspectionItemsLight({
      inspectionId: this.inspectionId,
    });
  }

  private mapToInspectionEvent(formData: any): InspectionEvent {
    const startLocalDateTime = formData.startDate;

    const endLocalDateTime = formData.sameStartAndEndDate
      ? formData.startDate
      : formData.endDate || null;

    const inspectionEvent = {
      inspectionId: this.inspectionId,
      optionId: formData.option,
      itemId: formData.item,
      remarks: formData.remarks,
      printInReport: formData.printInReport,
      startDate: new Date(startLocalDateTime),
      endDate: endLocalDateTime ? new Date(endLocalDateTime) : null,
    } as InspectionEvent;
    return inspectionEvent;
  }

  private findEventOptionByName(eventOptionName: string): Option {
    return eventOptionName
      ? this.inspectionEventOptions.find(
          (op) => op.appEventName === eventOptionName,
        )
      : null;
  }

  private openLoadingDialog(message: string) {
    return this.dialog.open(LoadingDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: message,
    });
  }
}
